/**
 * A context for the Keyword List Composer.
 * That's an initial version of the state and, hence, not optimized for performance. In the future versions, some of the
 * state will be moved to components.
 */
import { type Dispatch, type ReactNode, createContext, useCallback, useContext, useMemo, useReducer } from "react";

import noop from "lodash/noop";

import countryService, { type ICountryObject } from "app/services/CountryService";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";

import { createKeyword } from "./createKeyword";
import type { Mode, OrderBy, Keyword } from "./types";
import { getSortedKeywords } from "./utils/getSortedKeywords";

const WORLDWIDE = countryService.getCountryById(999);

function resolveMode(seedKeyword: string | undefined, keywordGroupToEdit: IUserKeywordGroup | undefined): Mode {
    if (keywordGroupToEdit) {
        return "edit_existing_list";
    }
    if (seedKeyword) {
        return "init_with_seed";
    }
    return "empty_list";
}

type SuggestionsStatus = "idle" | "pending" | "success" | "error";

type Action =
    | { type: "CHANGE_SUGGESTIONS_STATUS"; payload: SuggestionsStatus }
    | { type: "RESET_ACTIONS_COUNT" }
    | { type: "MARK_SHOULD_CONFIRM_BEFORE_CLOSE" }
    | { type: "CHANGE_MODE"; payload: Mode }
    | { type: "CHANGE_COUNTRY"; payload: ICountryObject }
    | { type: "CHANGE_SORTING"; payload: OrderBy }
    | { type: "POPULATE_KEYWORDS"; payload: Keyword[] }
    | { type: "PREPEND_KEYWORDS"; payload: Keyword[] }
    | { type: "DESELECT_ALL_KEYWORDS" }
    | { type: "SELECT_KEYWORDS"; payload: { keywords: Keyword[]; selected: boolean } }
    | { type: "REMOVE_KEYWORDS"; payload: Keyword[] }
    | { type: "UPDATE_KEYWORDS_VOLUMES"; payload: Keyword[] }
    | { type: "CHANGE_QUERY"; payload: string };

interface State {
    suggestionsStatus: SuggestionsStatus;
    keywordGroupToEdit: IUserKeywordGroup | undefined;
    country: ICountryObject;
    mode: Mode;
    bulkEditMode: boolean;
    actionsCount: number;
    isCloseConfirmationForced: boolean;
    orderBy: OrderBy;
    keywords: Keyword[];
    query: string;
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "CHANGE_SUGGESTIONS_STATUS": {
            return { ...state, suggestionsStatus: action.payload };
        }
        case "RESET_ACTIONS_COUNT": {
            return { ...state, actionsCount: 0 };
        }
        case "MARK_SHOULD_CONFIRM_BEFORE_CLOSE": {
            return { ...state, isCloseConfirmationForced: true };
        }
        case "CHANGE_MODE": {
            return { ...state, mode: action.payload };
        }
        case "CHANGE_COUNTRY": {
            return { ...state, country: action.payload };
        }
        case "CHANGE_SORTING": {
            return { ...state, orderBy: action.payload, actionsCount: state.actionsCount + 1 };
        }
        case "POPULATE_KEYWORDS": {
            const existingKeywords = new Set(state.keywords.map((keyword) => keyword.term));
            const newKeywords = action.payload.filter((keyword) => !existingKeywords.has(keyword.term));
            return { ...state, keywords: [...state.keywords, ...newKeywords] };
        }
        case "PREPEND_KEYWORDS": {
            if (state.keywords.length > 0 && state.keywords[0].isSeed) {
                return {
                    ...state,
                    keywords: [state.keywords[0], ...action.payload, ...state.keywords.slice(1)],
                    actionsCount: state.actionsCount + 1,
                };
            }

            return {
                ...state,
                keywords: [...action.payload, ...state.keywords],
                actionsCount: state.actionsCount + 1,
            };
        }
        case "SELECT_KEYWORDS": {
            const { keywords, selected } = action.payload;
            const selectedKeywordsSet = new Set(keywords.map((keyword) => keyword.term));
            const newKeywords = state.keywords.map((keyword) =>
                selectedKeywordsSet.has(keyword.term) ? { ...keyword, selected } : keyword,
            );
            const bulkEditMode = newKeywords.some((keyword) => keyword.selected);
            return { ...state, keywords: newKeywords, bulkEditMode };
        }
        case "DESELECT_ALL_KEYWORDS": {
            return {
                ...state,
                keywords: state.keywords.map((keyword) => ({ ...keyword, selected: false })),
                bulkEditMode: false,
            };
        }
        case "REMOVE_KEYWORDS": {
            const selectedKeywords = new Set(action.payload.map((keyword) => keyword.term));
            const keywords = state.keywords.filter((keyword) => !selectedKeywords.has(keyword.term));
            return {
                ...state,
                keywords,
                actionsCount: state.actionsCount + 1,
            };
        }
        case "UPDATE_KEYWORDS_VOLUMES": {
            const volumes = new Map(action.payload.map((keyword) => [keyword.term, keyword.volume]));
            const newKeywords = state.keywords.map((keyword) =>
                volumes.has(keyword.term)
                    ? { ...keyword, volume: volumes.get(keyword.term), isLoading: false }
                    : keyword,
            );
            return { ...state, keywords: newKeywords };
        }
        case "CHANGE_QUERY": {
            return { ...state, query: action.payload };
        }
        default:
            return state;
    }
}

interface KeywordListComposerContextShape {
    state: State;
    dispatch: Dispatch<Action>;
}

const CONFIRM_BEFORE_CLOSE_THRESHOLD = 3;

function createInitialState({
    seedKeyword,
    keywordGroupToEdit,
    country = WORLDWIDE,
}: {
    seedKeyword?: string;
    keywordGroupToEdit?: IUserKeywordGroup;
    country?: ICountryObject;
}): State {
    const mode = resolveMode(seedKeyword, keywordGroupToEdit);
    const orderBy: OrderBy = { sortDirection: "desc", field: "volume" };
    const initKeywords =
        keywordGroupToEdit?.Keywords?.map((keyword) => createKeyword({ term: keyword, isLoading: true })) ?? [];

    if (seedKeyword) {
        initKeywords.unshift(createKeyword({ term: seedKeyword, isLoading: true, isSeed: true }));
    }

    return {
        suggestionsStatus: "idle",
        keywordGroupToEdit,
        country,
        mode,
        bulkEditMode: false,
        actionsCount: 0,
        isCloseConfirmationForced: false,
        orderBy,
        keywords: getSortedKeywords(initKeywords, orderBy),
        query: "",
    };
}

const KeywordListComposerContext = createContext<KeywordListComposerContextShape>({
    state: createInitialState({}),
    dispatch: noop,
});

interface KeywordListComposerProviderProps {
    seedKeyword?: string;
    keywordGroupToEdit?: IUserKeywordGroup;
    country?: ICountryObject;
    children: ReactNode | ReactNode[];
}

export const KeywordListComposerProvider = ({
    seedKeyword,
    keywordGroupToEdit,
    country,
    children,
}: KeywordListComposerProviderProps) => {
    const [state, dispatch] = useReducer(reducer, { seedKeyword, keywordGroupToEdit, country }, createInitialState);
    const value = useMemo(() => ({ state, dispatch }), [state]);
    return <KeywordListComposerContext.Provider value={value}>{children}</KeywordListComposerContext.Provider>;
};

export const useKeywordListComposerContext = () => {
    const { state, dispatch } = useContext(KeywordListComposerContext);

    const seedKeyword = useMemo(() => {
        return state.keywords.find((keyword) => keyword.isSeed)?.term;
    }, [state.keywords]);

    const markShouldConfirmBeforeClose = useCallback(() => {
        dispatch({ type: "MARK_SHOULD_CONFIRM_BEFORE_CLOSE" });
    }, [dispatch]);

    const changeSuggestionsStatus = useCallback(
        (suggestionsStatus: SuggestionsStatus) => {
            dispatch({ type: "CHANGE_SUGGESTIONS_STATUS", payload: suggestionsStatus });
        },
        [dispatch],
    );

    const changeMode = useCallback(
        (mode: Mode) => {
            dispatch({ type: "CHANGE_MODE", payload: mode });
        },
        [dispatch],
    );

    const changeCountry = useCallback(
        (country: ICountryObject) => {
            dispatch({ type: "CHANGE_COUNTRY", payload: country });
        },
        [dispatch],
    );

    const changeSorting = useCallback(
        (orderBy: OrderBy) => {
            dispatch({ type: "CHANGE_SORTING", payload: orderBy });
        },
        [dispatch],
    );

    const loadingKeywords = useMemo(() => {
        return state.keywords.filter((keyword) => keyword.isLoading);
    }, [state.keywords]);

    const removeKeywords = useCallback(
        (keywords: Keyword[]) => {
            dispatch({ type: "REMOVE_KEYWORDS", payload: keywords });
        },
        [dispatch],
    );

    const prependKeywords = useCallback(
        (keywords: Keyword[]) => {
            dispatch({ type: "PREPEND_KEYWORDS", payload: keywords });
        },
        [dispatch],
    );

    const populateKeywords = useCallback(
        (keywords: Keyword[]) => {
            dispatch({ type: "POPULATE_KEYWORDS", payload: keywords });
        },
        [dispatch],
    );

    const updateKeywordsVolumes = useCallback(
        (keywords: Keyword[]) => {
            dispatch({ type: "UPDATE_KEYWORDS_VOLUMES", payload: keywords });
        },
        [dispatch],
    );

    const keywordTerms = useMemo(
        () => new Set(state.keywords.map((keyword) => keyword.term.toLocaleLowerCase())),
        [state.keywords],
    );

    const checkIfKeywordExists = useCallback(
        (keyword: Keyword | string) => {
            const term = typeof keyword === "string" ? keyword : keyword.term;
            return keywordTerms.has(term.toLocaleLowerCase());
        },
        [keywordTerms],
    );

    const selectKeywords = useCallback(
        (keywords: Keyword[], selected: boolean) => {
            dispatch({ type: "SELECT_KEYWORDS", payload: { keywords, selected } });
        },
        [dispatch],
    );

    const deselectAllKeywords = useCallback(() => {
        dispatch({ type: "DESELECT_ALL_KEYWORDS" });
    }, [dispatch]);

    const changeQuery = useCallback(
        (query: string) => {
            dispatch({ type: "CHANGE_QUERY", payload: query });
        },
        [dispatch],
    );

    return {
        suggestionsStatus: state.suggestionsStatus,
        changeSuggestionsStatus,

        seedKeyword,
        keywordGroupToEdit: state.keywordGroupToEdit,
        shouldConfirmBeforeClose:
            state.isCloseConfirmationForced || state.actionsCount > CONFIRM_BEFORE_CLOSE_THRESHOLD,
        markShouldConfirmBeforeClose,

        keywords: state.keywords,
        loadingKeywords,
        populateKeywords,
        prependKeywords,
        removeKeywords,
        updateKeywordsVolumes,
        checkIfKeywordExists,

        mode: state.mode,
        changeMode,

        query: state.query,
        changeQuery,

        country: state.country,
        changeCountry,

        orderBy: state.orderBy,
        changeSorting,

        bulkEditMode: state.bulkEditMode,
        selectKeywords,
        deselectAllKeywords,
    };
};
